import { PageLayout } from '@/layouts/page-layout'
import { useGetAssetCategoriesQuery, useGetAssetTypesQuery, useCreateAssetV2Mutation } from '@/store/services'
import { useEffect, useMemo, useState } from 'react'
import { Button, Input, Typography } from '@material-tailwind/react'
import { Link, useNavigate } from 'react-router-dom'
import { list } from '@/router/utils'
import { Formik, Form } from 'formik'
import { ContractSearcher } from '@/components/contract-searcher'
import { ContractSearcherContext } from '@/components/contract-searcher/context'
import { HasAccess } from '@permify/react-role'

export const AssetV2Create = () => {
  const navigate = useNavigate()
  const { data: { asset_categories: categories = [] } = {} } = useGetAssetCategoriesQuery()
  const [categoryId, setCategoryId] = useState(null)
  const { data: { asset_types: types = [] } = {} } = useGetAssetTypesQuery(categoryId, { skip: !categoryId })
  const [typeId, setTypeId] = useState(null)
  const [categoryFields, setCategoryFields] = useState({})
  const [typeFields, setTypeFields] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [createAsset, { isLoading }] = useCreateAssetV2Mutation()

  useEffect(() => {
    if (categories.length && !categoryId) setCategoryId(categories[0].id)
  }, [categories])

  const selectedType = useMemo(
    () => types.find((t) => t.id === typeId),
    [types, typeId]
  )

  const fieldsCategory = useMemo(() => {
    const fields = (categories.find((c) => c.id === categoryId)?.custom_fields || [])
    return fields.filter((f) => !f?.archived)
  }, [categories, categoryId])

  useEffect(() => {
    if (types.length === 1) {
      const onlyTypeId = types[0].id
      if (typeId !== onlyTypeId) setTypeId(onlyTypeId)
    }
  }, [types, typeId])

  const fieldsType = useMemo(() => (selectedType?.custom_fields || []).filter((f) => !f?.archived), [selectedType])

  const handleFieldChange = (field, value, scope = 'type') => {
    if (scope === 'category') {
      setCategoryFields((prev) => ({ ...prev, [field]: value }))
    } else {
      setTypeFields((prev) => ({ ...prev, [field]: value }))
    }
  }

  return (
    <PageLayout title='Crear activo 2.0'>
      <Formik
        enableReinitialize
        initialValues={{
          contractor_contract_id: null,
          identifier_value: ''
        }}
        onSubmit={async (values) => {
          setSubmitError('')
          try {
            await createAsset({
              asset_type_id: typeId,
              contractor_contract_id: values.contractor_contract_id,
              identifier_value: values.identifier_value,
              category_fields: categoryFields,
              type_fields: typeFields
            }).unwrap()
            navigate(list('assets_v2'))
          } catch (err) {
            const msg = err?.data?.errors?.join(', ') || 'Error al crear activo'
            setSubmitError(msg)
          }
        }}
      >
        {({ setFieldValue, handleChange, values }) => (
          <Form className='flex flex-col gap-8'>
            {submitError && (
              <div className='text-red-600 bg-red-50 border border-red-200 rounded p-3'>
                {submitError}
              </div>
            )}

            <div className='grid grid-cols-3 gap-8'>
              <div className='col-span-1 flex flex-col gap-2'>
                <Typography variant='h5' className='text-left'>General</Typography>
                <Typography variant='small' className='text-gray-600 text-left'>
                  Rellena la información general del activo.
                </Typography>
              </div>
              <div className='col-span-2 flex flex-col gap-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='col-span-2'>
                    <p className='text-sm font-medium text-gray-700 mb-1'>Contratista / Contrato</p>
                    <div className='grid grid-cols-2 gap-3'>
                      <ContractSearcherContext.Provider value={{ currentContract: {} }}>
                        <ContractSearcher
                          required
                          name="contractor_contract_id"
                          onChange={(selected) => setFieldValue('contractor_contract_id', selected?.id)}
                        />
                      </ContractSearcherContext.Provider>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='grid grid-cols-3 gap-8'>
              <div className='col-span-1 flex flex-col gap-2'>
                <Typography variant='h5' className='text-left'>Clasificación</Typography>
                <Typography variant='small' className='text-gray-600 text-left'>
                  Selecciona categoría y tipo del activo.
                </Typography>
              </div>
              <div className='col-span-2 grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-gray-700'>Categoría</label>
                  <select
                    className='select w-full'
                    value={categoryId || ''}
                    onChange={(e) => { setCategoryId(Number(e.target.value)); setTypeId(null); setTypeFields({}) }}
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-700'>Tipo</label>
                  <select
                    className='select w-full'
                    value={typeId || ''}
                    onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : null)}
                    disabled={!types.length}
                  >
                    {types.length !== 1 && <option value=''>{'<Seleccione Tipo>'}</option>}
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {fieldsCategory.length > 0 && (
              <div className='grid grid-cols-3 gap-8'>
                <div className='col-span-1 flex flex-col gap-2'></div>
                <div className='col-span-2 bg-gray-100 rounded-2xl p-6 grid grid-cols-2 gap-4'>
                  <div className='col-span-2 flex flex-col gap-1'>
                    <Typography variant='h5' className='text-left'>Datos {categories.find((c) => c.id === categoryId)?.name || 'categoría'}</Typography>
                    <Typography variant='small' className='text-gray-600 text-left'>
                      Rellena los campos de la categoría.
                    </Typography>
                  </div>
                  {fieldsCategory.map((field) => (
                    <Input
                      key={field.key}
                      label={`${field.label}${field.required ? ' *' : ''}`}
                      required={field.required}
                      onChange={(e) => handleFieldChange(field.key, e.target.value, 'category')}
                    />
                  ))}
                </div>
              </div>
            )}

            {typeId && selectedType && (
              <div className='grid grid-cols-3 gap-8'>
                <div className='col-span-1 flex flex-col gap-2'></div>
                <div className='col-span-2 bg-gray-100 rounded-2xl p-6 grid grid-cols-2 gap-4'>
                  <div className='col-span-2 flex flex-col gap-1'>
                    <Typography variant='h5' className='text-left'>Datos {selectedType?.name || 'tipo'}</Typography>
                    <Typography variant='small' className='text-gray-600 text-left'>
                      Rellena los campos del tipo y el identificador.
                    </Typography>
                  </div>
                  <Input
                    value={values.identifier_value}
                    onChange={handleChange}
                    name="identifier_value"
                    required
                    label={selectedType?.identifier_label || 'Identificador'}
                  />
                  {fieldsType.map((field) => (
                    <Input
                      key={field.key}
                      label={`${field.label}${field.required ? ' *' : ''}`}
                      required={field.required}
                      onChange={(e) => handleFieldChange(field.key, e.target.value, 'type')}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className='flex justify-end gap-3 mt-4'>
              <Link to={list('assets_v2')}>
                <Button variant='text'>Cancelar</Button>
              </Link>
              <HasAccess permissions='asset_v2#create'>
                <Button type='submit' loading={isLoading} disabled={!typeId}>Guardar</Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}
